<script setup>
// As execuções de disparo (lotes), mais recente primeiro.
//
// A unidade que o operador escolhe é a execução, não o template: o mesmo
// template sai por agentes diferentes, em horários diferentes, para listas
// diferentes. Por isso a linha mostra agente e horário com o mesmo peso do
// nome do template.
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import outboundsAPI from 'dashboard/api/outbounds';
import { whenLabel, fullTimestamp } from '../helpers/outboundHistory';

const PAGE_SIZE = 25;

defineEmits(['select']);

const { t } = useI18n();
const store = useStore();
const accountId = store.getters.getCurrentAccountId;

const lotes = ref([]);
const total = ref(0);
const offset = ref(0);
const loading = ref(false);

async function load() {
  loading.value = true;
  try {
    const payload = await outboundsAPI.historyLotes(accountId, {
      limit: PAGE_SIZE,
      offset: offset.value,
    });
    lotes.value = payload?.lotes || [];
    total.value = payload?.total || 0;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('OUTBOUNDS.HISTORY.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

function goTo(nextOffset) {
  offset.value = Math.max(0, nextOffset);
  load();
}

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-3">
    <p v-if="loading" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.HISTORY.LOADING') }}
    </p>

    <p v-else-if="!lotes.length" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.HISTORY.EMPTY') }}
    </p>

    <template v-else>
      <div class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full text-sm">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.HISTORY.COLUMNS.WHEN') }}
              </th>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.HISTORY.COLUMNS.AGENT') }}
              </th>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.HISTORY.COLUMNS.TEMPLATE') }}
              </th>
              <th class="px-3 py-2 font-medium text-right">
                {{ t('OUTBOUNDS.HISTORY.COLUMNS.PEOPLE') }}
              </th>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.HISTORY.COLUMNS.OUTCOME') }}
              </th>
              <th class="w-8 px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="lote in lotes"
              :key="lote.id_lote"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="$emit('select', lote)"
            >
              <td
                class="px-3 py-2 text-n-slate-12"
                :title="fullTimestamp(lote.inicio)"
              >
                {{ whenLabel(lote.inicio, t) }}
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{ lote.chatbot_name || `#${lote.chatbot_id}` }}
                <span
                  v-if="lote.agent_phone"
                  class="block text-xs text-n-slate-10 tabular-nums"
                >
                  {{ lote.agent_phone }}
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{ lote.template_name }}
                <span
                  v-if="lote.segmento"
                  class="block text-xs text-n-slate-10"
                >
                  {{ lote.segmento }}
                </span>
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-12">
                {{ lote.total }}
              </td>
              <td class="px-3 py-2">
                <span class="flex flex-wrap gap-1">
                  <span
                    v-if="lote.enviados"
                    class="px-2 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11 tabular-nums"
                  >
                    {{
                      t(
                        'OUTBOUNDS.HISTORY.SENT_N',
                        { n: lote.enviados },
                        lote.enviados
                      )
                    }}
                  </span>
                  <span
                    v-if="lote.erros"
                    class="px-2 py-0.5 text-xs rounded-full bg-n-ruby-3 text-n-ruby-11 tabular-nums"
                  >
                    {{
                      t(
                        'OUTBOUNDS.HISTORY.FAILED_N',
                        { n: lote.erros },
                        lote.erros
                      )
                    }}
                  </span>
                  <span
                    v-if="lote.pulados"
                    class="px-2 py-0.5 text-xs rounded-full bg-n-amber-3 text-n-amber-11 tabular-nums"
                  >
                    {{
                      t(
                        'OUTBOUNDS.HISTORY.SKIPPED_N',
                        { n: lote.pulados },
                        lote.pulados
                      )
                    }}
                  </span>
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-10">
                <i class="i-lucide-chevron-right size-4" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="total > PAGE_SIZE"
        class="flex items-center justify-between text-sm text-n-slate-11"
      >
        <span class="tabular-nums">
          {{
            t('OUTBOUNDS.HISTORY.RANGE', {
              from: offset + 1,
              to: offset + lotes.length,
              total,
            })
          }}
        </span>
        <span class="flex gap-2">
          <button
            type="button"
            class="px-3 py-1 border rounded-lg border-n-weak disabled:opacity-40"
            :disabled="offset === 0 || loading"
            @click="goTo(offset - PAGE_SIZE)"
          >
            {{ t('OUTBOUNDS.HISTORY.PREV') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 border rounded-lg border-n-weak disabled:opacity-40"
            :disabled="offset + lotes.length >= total || loading"
            @click="goTo(offset + PAGE_SIZE)"
          >
            {{ t('OUTBOUNDS.HISTORY.NEXT') }}
          </button>
        </span>
      </div>
    </template>
  </div>
</template>
